import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Collapsible, Flex, IconButton, Image, Stack, Text } from '@chakra-ui/react';
import { useIntl } from 'react-intl';
import { LuChevronDown, LuChevronRight, LuPanelLeftClose, LuPanelLeftOpen } from 'react-icons/lu';
import { useState } from 'react';
import type { UserProfile } from '@/client';
import { useColorModeValue } from '@/components/ui/color-mode';
import { Tooltip } from '@/components/ui/tooltip';
import emblemBlack from '@/assets/emblem/black.png';
import emblemCream from '@/assets/emblem/cream.png';
import logoLight from '@/assets/logo/light.png';
import logoDark from '@/assets/logo/dark.png';
import { useActivityBadge } from '@/screens/activity/hooks/useActivityBadge';
import { TOUR_ANCHORS } from '@/features/product-tour';
import {
    SIDEBAR_PAD,
    SIDEBAR_RAIL_PAD,
    SIDEBAR_RAIL_W,
    SIDEBAR_ROW,
    SIDEBAR_W,
} from '../layoutTokens';
import { groupSections, sectionPath } from './navConfig';
import { ProductSwitcher } from './ProductSwitcher';
import { SidebarNavItem } from './SidebarNavItem';
import { SidebarUserCard } from './SidebarUserCard';
import type { WorkspaceNav } from './useWorkspaceNav';

interface AppSidebarProps {
    nav: WorkspaceNav;
    user: UserProfile;
    collapsed?: boolean;
    onToggleCollapsed: () => void;
}

export function AppSidebar({ nav, user, collapsed = false, onToggleCollapsed }: AppSidebarProps) {
    const intl = useIntl();
    const { pathname } = useLocation();
    const {
        workspace,
        sections,
        secondary,
        activeSectionId,
        prefsPanes,
        activePrefsPaneId,
        inPreferences,
    } = nav;
    const preferencesMenuKey = `${workspace?.stage ?? 'manage'}:${workspace?.id ?? ''}`;
    const [preferencesExpansion, setPreferencesExpansion] = useState<Record<string, boolean>>({});
    const preferencesExpanded = preferencesExpansion[preferencesMenuKey] ?? inPreferences;
    const setPreferencesExpanded = (open: boolean) => {
        setPreferencesExpansion((current) => ({ ...current, [preferencesMenuKey]: open }));
    };

    // One section, one badge. It counts open loops only — never the record, and
    // never unread rows, so zero is reachable and means something.
    const needsCount = useActivityBadge(
        workspace ? { type: workspace.kind === 'home' ? 'home' : 'group', id: workspace.id } : null,
    );

    const stage = workspace?.stage ?? 'manage';
    const homeLink = workspace?.root ?? '/';
    const pad = collapsed ? SIDEBAR_RAIL_PAD : SIDEBAR_PAD;

    // The wordmark the header used to carry. The rail has no room for it, so it
    // falls back to the emblem on its own.
    const logo = useColorModeValue(logoLight, logoDark);
    const emblem = useColorModeValue(emblemBlack, emblemCream);
    const logoAlt = intl.formatMessage({
        id: '920422d5-2f6e-4461-8732-dac8ee878c77',
        defaultMessage: 'CoHaven',
    });
    const toggleLabel = intl.formatMessage(
        collapsed
            ? {
                  id: '71655d84-a15b-40f8-a3d4-e2fc8c83275e',
                  defaultMessage: 'Expand sidebar',
              }
            : {
                  id: 'a063b7ec-5c83-43e8-af94-cbc940582d80',
                  defaultMessage: 'Collapse sidebar',
              },
    );
    const preferencesToggleLabel = intl.formatMessage(
        preferencesExpanded
            ? {
                  id: 'f3a54c71-9e20-4d86-b521-0c7a8e3f6d94',
                  defaultMessage: 'Collapse settings menu',
              }
            : {
                  id: 'c8e16b43-5f92-4a70-a6d1-2b9c4e7f3058',
                  defaultMessage: 'Expand settings menu',
              },
    );

    return (
        <Flex
            as="nav"
            aria-label={intl.formatMessage({
                id: 'b62d8f14-3a09-4c75-8e21-d0f5b7c9a643',
                defaultMessage: 'Sections',
            })}
            direction="column"
            h="full"
            w={collapsed ? SIDEBAR_RAIL_W : SIDEBAR_W}
            flexShrink={0}
            overflow="hidden"
            transition="width 0.2s ease-out"
            _motionReduce={{ transition: 'none' }}
            bg="bg.canvas"
            borderRightWidth="1px"
            borderColor="border.chrome"
            pt={pad.pt}
            px={pad.px}
            pb={pad.pb}
        >
            <Stack align={collapsed ? 'center' : 'flex-start'} gap={3} pb={6} flexShrink={0}>
                <Flex
                    w="full"
                    direction={collapsed ? 'column' : 'row'}
                    align="center"
                    justify={collapsed ? 'center' : 'space-between'}
                    gap={2}
                >
                    <RouterLink
                        to={homeLink}
                        aria-label={logoAlt}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        <Image
                            src={collapsed ? emblem : logo}
                            alt={logoAlt}
                            aria-hidden="true"
                            h={collapsed ? 7 : 6}
                        />
                    </RouterLink>

                    <Tooltip
                        content={toggleLabel}
                        positioning={{ placement: collapsed ? 'right' : 'bottom' }}
                        openDelay={200}
                    >
                        <IconButton
                            size="xs"
                            variant="ghost"
                            color="fg.nav"
                            aria-label={toggleLabel}
                            onClick={onToggleCollapsed}
                        >
                            {collapsed ? <LuPanelLeftOpen /> : <LuPanelLeftClose />}
                        </IconButton>
                    </Tooltip>
                </Flex>

                <ProductSwitcher
                    stage={stage}
                    homes={nav.homes}
                    groups={nav.groups}
                    collapsed={collapsed}
                />
            </Stack>

            <Box data-tour={TOUR_ANCHORS.sidebarNav} flex={1} overflowY="auto" overflowX="hidden">
                {groupSections(sections).map((group, index) => (
                    <Box key={group.group?.id ?? group.items.map((section) => section.id).join('-')} mb={1}>
                        {collapsed ? (
                            index > 0 && group.group && <Box h="1px" bg="border.chrome" mx={1.5} my={2} />
                        ) : group.group ? (
                            <Text
                                fontSize="9.5px"
                                fontWeight="bold"
                                letterSpacing="0.9px"
                                color="fg.subtle"
                                px={SIDEBAR_ROW.px}
                                pt={2}
                                pb={1}
                                textTransform="uppercase"
                            >
                                {intl.formatMessage(group.group)}
                            </Text>
                        ) : null}

                        <Stack gap={0.5}>
                            {group.items.map((section) => {
                                const active = activeSectionId === section.id;
                                const isPreferences = section.id === 'preferences';
                                const preferencesPath = sectionPath(stage, workspace?.id ?? '', section);
                                return (
                                    <Box key={section.id}>
                                        {isPreferences && !collapsed && prefsPanes.length > 0 ? (
                                            <Collapsible.Root
                                                open={preferencesExpanded}
                                                onOpenChange={({ open }) => setPreferencesExpanded(open)}
                                            >
                                                <SidebarNavItem
                                                    icon={section.icon}
                                                    label={intl.formatMessage(section.label)}
                                                    to={preferencesPath}
                                                    active={active}
                                                    onNavigate={() => setPreferencesExpanded(true)}
                                                    endAction={(
                                                        <Tooltip content={preferencesToggleLabel} positioning={{ placement: 'right' }} openDelay={200}>
                                                            <Collapsible.Trigger asChild>
                                                                <IconButton
                                                                    size="2xs"
                                                                    variant="ghost"
                                                                    color="fg.nav"
                                                                    aria-label={preferencesToggleLabel}
                                                                >
                                                                    {preferencesExpanded ? <LuChevronDown /> : <LuChevronRight />}
                                                                </IconButton>
                                                            </Collapsible.Trigger>
                                                        </Tooltip>
                                                    )}
                                                />

                                                <Collapsible.Content>
                                                    <Stack
                                                        gap="0.5"
                                                        mt="1"
                                                        mb="2"
                                                        ml="4"
                                                        pl="2"
                                                        borderLeftWidth="1px"
                                                        borderColor="border.chrome"
                                                    >
                                                        {prefsPanes.map((pane) => {
                                                            const paneActive = activePrefsPaneId === pane.id;
                                                            return (
                                                                <RouterLink
                                                                    key={pane.id}
                                                                    to={`${preferencesPath}/${pane.path}`}
                                                                    aria-current={paneActive ? 'page' : undefined}
                                                                >
                                                                    <Text
                                                                        fontSize="12px"
                                                                        fontWeight={paneActive ? 'bold' : 'medium'}
                                                                        color={paneActive ? 'fg' : 'fg.nav'}
                                                                        bg={paneActive ? 'gold.wash' : 'transparent'}
                                                                        px="2"
                                                                        py="1"
                                                                        borderRadius="md"
                                                                        _hover={{ bg: paneActive ? 'gold.wash' : 'bg.rowHover' }}
                                                                    >
                                                                        {intl.formatMessage(pane.label)}
                                                                    </Text>
                                                                </RouterLink>
                                                            );
                                                        })}
                                                    </Stack>
                                                </Collapsible.Content>
                                            </Collapsible.Root>
                                        ) : (
                                            <SidebarNavItem
                                                icon={section.icon}
                                                label={intl.formatMessage(section.label)}
                                                to={preferencesPath}
                                                active={active}
                                                collapsed={collapsed}
                                                badge={section.id === 'activity' ? needsCount : undefined}
                                            />
                                        )}
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                ))}
            </Box>

            <Stack gap={0.5} pt={2} flexShrink={0}>
                {secondary.map((item) => {
                    const to = item.external ? item.path : sectionPath(stage, workspace?.id ?? '', item);
                    return (
                        <SidebarNavItem
                            key={item.id}
                            icon={item.icon}
                            label={intl.formatMessage(item.label)}
                            to={to}
                            external={item.external}
                            active={!item.external && pathname === to}
                            collapsed={collapsed}
                        />
                    );
                })}
            </Stack>

            <Box borderTopWidth="1px" borderColor="border.chrome" pt={4} mt={2.5} flexShrink={0}>
                <SidebarUserCard
                    user={user}
                    meta={workspace?.name}
                    workspace={workspace}
                    homes={nav.homes}
                    groups={nav.groups}
                    collapsed={collapsed}
                />
            </Box>
        </Flex>
    );
}
